import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useDocumentBinaryPreview } from "src-next/modules/patient-supporting-documents";

import { CommonDialog } from "@/components/common/CommonDialog";
import { PrimaryButton } from "@/components/common";

import { PatientSupportingDocumentRecord } from "./types";

interface PatientSupportingDocumentPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  /** Documento real anexo (dataUrl + mimeType) — sem placeholder. */
  document: PatientSupportingDocumentRecord | null;
  onReplace?: (document: PatientSupportingDocumentRecord) => void;
}

/**
 * Modal de visualização do arquivo anexado.
 * Usa CommonDialog; data URLs grandes falham com window.open.
 * Binary é resolvido sob demanda (lazy) quando o modal abre.
 */
export function PatientSupportingDocumentPreviewDialog({
  open,
  onClose,
  document: doc,
  onReplace,
}: PatientSupportingDocumentPreviewDialogProps) {
  const {
    dataUrl,
    mimeType: resolvedMimeType,
    isLoading,
  } = useDocumentBinaryPreview(doc, open && Boolean(doc));

  const mimeType = (resolvedMimeType || doc?.mimeType || "").toLowerCase();
  const previewUrl = dataUrl;
  const isImage =
    mimeType.startsWith("image/") || dataUrl.startsWith("data:image/");
  const isPdf =
    mimeType === "application/pdf" ||
    dataUrl.startsWith("data:application/pdf");

  return (
    <CommonDialog
      isOpen={open}
      handleClose={onClose}
      maxWidth="md"
      sheetTitle={doc?.description || doc?.fileName || "Visualizar documento"}
      sheetSubtitle={doc?.description ? doc.fileName : "Documento anexado"}
      sheetSize="full"
      footer={
        doc?.isRejected && onReplace ? (
          <Stack direction="row" justifyContent="flex-end" width="100%">
            <PrimaryButton
              width="fit-content"
              height="40px"
              onClick={() => onReplace(doc)}
            >
              Substituir documento
            </PrimaryButton>
          </Stack>
        ) : undefined
      }
    >
      {doc?.isRejected ? (
        <Alert severity="error" sx={{ mt: { xs: 0, md: 1 }, mb: 1.5 }}>
          {doc.rejectionReason?.trim()
            ? doc.rejectionReason
            : "Este documento foi recusado. Envie um novo arquivo."}
        </Alert>
      ) : null}
      <Box
        sx={{
          width: "100%",
          minHeight: { xs: 280, md: 420 },
          maxHeight: { xs: "70vh", md: "75vh" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
          bgcolor: "grey.100",
          borderRadius: 1,
          mt: 1,
        }}
      >
        {isLoading ? (
          <CircularProgress size={36} />
        ) : !previewUrl ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum arquivo disponível para visualização.
          </Typography>
        ) : isImage ? (
          <Box
            component="img"
            src={previewUrl}
            alt={doc?.fileName || "Documento anexado"}
            sx={{
              maxWidth: "100%",
              maxHeight: { xs: "65vh", md: "70vh" },
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : isPdf ? (
          <Box
            component="iframe"
            title={doc?.fileName || "PDF anexado"}
            src={previewUrl}
            sx={{
              width: "100%",
              height: { xs: "65vh", md: "70vh" },
              border: 0,
              bgcolor: "background.paper",
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" px={2} textAlign="center">
            Este tipo de arquivo não pode ser pré-visualizado aqui. Baixe ou abra
            pelo nome: {doc?.fileName}
          </Typography>
        )}
      </Box>
    </CommonDialog>
  );
}
